// Story 4.3 — visual subfilter editor. Route-only page (no Refine resource):
// useParams `id` is the communityId. Authz is enforced by the backend
// (ALLOWED_ROLES); the UI entry is gated by the community:edit ability.
import { useMemo } from 'react';
import { useParams, Link } from 'react-router';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode } from '@lebinatikra/rtl-utils';
import { FilterApiError } from '../../lib/filter-api-client';
import { policyApi, type PolicyFormWire } from '../../lib/policy-api';
import { PrStatusList, prChangesQueryKey } from './pr-status-list';

const PROFILES = ['hasidic', 'litvish', 'sephardic', 'modern_orthodox', 'custom'] as const;
const ROLE_KEYS = ['children', 'bachurim', 'seminary-girls', 'avrechim', 'parents'] as const;

interface EditorFormValues {
  profile: string;
  additional_block_rules: string[];
  roles: Record<string, string>;
  rabbi_name: string;
  rabbi_redirect_note: string;
}

function linesToArray(text: string | undefined): string[] {
  return (text ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

export function PolicyEditor() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const communityId = id ?? '';
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EditorFormValues>();

  const prefill = useQuery({
    queryKey: ['policy-prefill', communityId],
    queryFn: () => policyApi.getPolicy(communityId),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  const initialValues = useMemo<EditorFormValues | undefined>(() => {
    if (!prefill.data) return undefined;
    const current = prefill.data.current;
    const roles: Record<string, string> = {};
    for (const role of ROLE_KEYS) {
      roles[role] = (current?.role_overrides?.[role] ?? []).join('\n');
    }
    return {
      profile: current?.profile ?? prefill.data.community.profile,
      additional_block_rules: current?.additional_block_rules ?? [],
      roles,
      rabbi_name: current?.rabbi_contact?.name ?? '',
      rabbi_redirect_note: current?.rabbi_contact?.redirect_note ?? '',
    };
  }, [prefill.data]);

  const submit = useMutation({
    mutationFn: (values: EditorFormValues) => {
      const roleOverrides: Record<string, string[]> = {};
      for (const role of ROLE_KEYS) {
        const lines = linesToArray(values.roles?.[role]);
        if (lines.length > 0) roleOverrides[role] = lines;
      }
      const wire: PolicyFormWire = {
        profile: values.profile,
        additional_block_rules: (values.additional_block_rules ?? [])
          .map((r) => (r ?? '').trim())
          .filter((r) => r.length > 0),
        role_overrides: roleOverrides,
        rabbi_contact: {
          name: values.rabbi_name.trim(),
          redirect_note: values.rabbi_redirect_note.trim(),
        },
      };
      return policyApi.submit(communityId, wire);
    },
    onSuccess: (created) => {
      notification.success({
        message: t('policy_editor.submit_success'),
        description: t('policy_editor.submit_success_pr', { number: created.pr_number }),
      });
      void queryClient.invalidateQueries({ queryKey: prChangesQueryKey(communityId) });
    },
    onError: (err) => {
      const message =
        err instanceof FilterApiError ? err.message : t('policy_editor.submit_error');
      notification.error({ message: t('policy_editor.submit_error'), description: message });
    },
  });

  // A failed prefill (403 for an unauthorized role, 404 for a deleted
  // community) leaves initialValues undefined — show the error + a way back
  // instead of an endless skeleton.
  if (prefill.error) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          type="error"
          message={(prefill.error as Error).message}
          data-testid="policy-editor-error"
        />
        <Link to={`/communities/${communityId}`}>
          <Button>{t('common.back')}</Button>
        </Link>
      </Space>
    );
  }

  if (prefill.isLoading || !initialValues) {
    return <Skeleton active data-testid="policy-editor-skeleton" />;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3}>
        <BidiText>
          {t('policy_editor.title', { name: prefill.data?.community.name ?? '' })}
        </BidiText>
      </Typography.Title>

      <Card title={t('policy_editor.section_form')}>
        <Form<EditorFormValues>
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={(values) => submit.mutate(values)}
        >
          <Form.Item
            name="profile"
            label={t('policy_editor.profile')}
            rules={[{ required: true }]}
          >
            <Select
              options={PROFILES.map((p) => ({
                value: p,
                label: t(`policy_editor.profiles.${p}`),
              }))}
            />
          </Form.Item>

          <Form.Item label={t('policy_editor.additional_block_rules')}>
            <Form.List name="additional_block_rules">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" style={{ width: '100%' }}>
                      <Form.Item {...field} noStyle>
                        <Input
                          style={{ width: 480 }}
                          placeholder={t('policy_editor.block_rule_placeholder')}
                        />
                      </Form.Item>
                      <Button
                        danger
                        onClick={() => remove(field.name)}
                        data-testid="remove-block-rule"
                      >
                        {t('common.delete')}
                      </Button>
                    </Space>
                  ))}
                  <Button
                    onClick={() => add('')}
                    data-testid="add-block-rule"
                  >
                    {t('policy_editor.add_block_rule')}
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>

          <Typography.Title level={5}>
            {t('policy_editor.role_overrides')}
          </Typography.Title>
          {ROLE_KEYS.map((role) => (
            <Form.Item
              key={role}
              name={['roles', role]}
              label={t(`policy_editor.roles.${role}`)}
            >
              <Input.TextArea
                rows={3}
                placeholder={t('policy_editor.role_override_placeholder')}
              />
            </Form.Item>
          ))}

          <Typography.Title level={5}>
            {t('policy_editor.rabbi_contact')}
          </Typography.Title>
          <Form.Item
            name="rabbi_name"
            label={t('policy_editor.rabbi_name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="rabbi_redirect_note"
            label={t('policy_editor.rabbi_redirect_note')}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={submit.isPending}
              data-testid="submit-policy"
            >
              {t('common.save')}
            </Button>
            <Link to={`/communities/${communityId}`}>
              <Button>{t('common.back')}</Button>
            </Link>
          </Space>
        </Form>
      </Card>

      <Card title={t('policy_editor.section_changes')}>
        <Typography.Paragraph type="secondary">
          {t('policy_editor.community_slug')}: <MonoCode>{prefill.data?.community.slug}</MonoCode>
        </Typography.Paragraph>
        <PrStatusList communityId={communityId} />
      </Card>
    </Space>
  );
}
